import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import { ChevronLeft, FileText, Upload, Eye } from 'lucide-react';
import { PORTAL_API } from './constants';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

const TZ_DEFAULT = 'America/Los_Angeles';

export function SupplierInvoiceAddTab({ invoiceData, token, onReload, currency = 'USD' }) {
  const [viewPODetail, setViewPODetail] = useState(null);
  const [sortState, handleSort] = useSortState('order_number', 'desc');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleUpload = async (file) => {
    if (!file) return;
    toast.info(`Uploading ${file.name}...`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`${PORTAL_API}/my-invoices/upload`, fd, { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data.message);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  if (viewPODetail) {
    return (
      <div>
        <button onClick={() => setViewPODetail(null)} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Orders
        </button>
        <h2 className="text-lg font-bold mb-4">Purchase Order: {viewPODetail.order_number}</h2>
        <div className="border rounded-lg bg-white p-5 mb-4">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div><span className="text-xs text-slate-500">Order Number</span><p className="font-mono font-bold">{viewPODetail.order_number}</p></div>
            <div><span className="text-xs text-slate-500">Status</span><p><Badge className="text-[10px]">{viewPODetail.status}</Badge></p></div>
            <div><span className="text-xs text-slate-500">Delivery Point</span><p>{viewPODetail.delivery_point || viewPODetail.delivery_to || '—'}</p></div>
            <div><span className="text-xs text-slate-500">Issued Date</span><p>{viewPODetail.created_at ? new Date(viewPODetail.created_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric', timeZone: TZ_DEFAULT}) : ''}</p></div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t">
            <div><span className="text-xs text-slate-500">Order Total</span><p className="font-mono font-bold text-lg">${(viewPODetail.order_total || viewPODetail.net_total || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</p></div>
            <div><span className="text-xs text-slate-500">Total Invoiced</span><p className="font-mono">${(viewPODetail.total_invoiced || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</p></div>
            <div><span className="text-xs text-slate-500">Amount Owed</span><p className="font-mono font-bold text-red-600">${(viewPODetail.total_owed || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</p></div>
          </div>
        </div>
        <div className="border rounded-lg bg-white overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b">
              <th className="text-left px-3 py-2 font-semibold">#</th>
              <th className="text-left px-3 py-2 font-semibold">Description</th>
              <th className="text-right px-3 py-2 font-semibold">Qty Ordered</th>
              <th className="text-right px-3 py-2 font-semibold">Unit Price</th>
              <th className="text-left px-3 py-2 font-semibold">Due Date</th>
              <th className="text-right px-3 py-2 font-semibold">Received</th>
              <th className="text-right px-3 py-2 font-semibold">Still Owed</th>
              <th className="text-right px-3 py-2 font-semibold">Line Total</th>
            </tr></thead>
            <tbody>
              {(viewPODetail.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2 text-xs">{idx + 1}</td>
                  <td className="px-3 py-2">{li.item_description || li.description || ''}</td>
                  <td className="px-3 py-2 text-right font-mono">{li.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono">${(li.unit_price || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</td>
                  <td className="px-3 py-2 text-xs">{li.due_date || li.item_due_date || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-600">{li.qty_received || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-600 font-semibold">{li.qty_owed || 0}</td>
                  <td className="px-3 py-2 text-right font-mono">${(li.net_price || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-3">
          <Button size="sm" variant="outline" onClick={async () => {
            try { const r = await api.get(`${PORTAL_API}/my-orders/${viewPODetail.order_number}/pdf`, { ...authHeaders, responseType: 'blob' }); const url = URL.createObjectURL(new Blob([r.data])); window.open(url); } catch { toast.error('PDF not available'); }
          }}><FileText className="w-3.5 h-3.5 mr-1" /> View PO PDF</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Add Invoice</h2>
        <span className="text-xs text-slate-500">Select an order to view details and add an invoice, or upload a PDF.</span>
      </div>
      <div
        onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files?.[0]); }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = '.pdf,.PDF'; input.onchange = (ev) => handleUpload(ev.target.files?.[0]); input.click(); }}
        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center mb-4 hover:border-indigo-400 hover:bg-slate-50 transition-all cursor-pointer"
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
        <p className="text-sm font-medium text-slate-700">Drag & drop invoice PDF here</p>
        <p className="text-xs text-slate-400 mt-1">or click to browse — or select an order below to view details</p>
      </div>
      <div className="border rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortableTableHeader label="Order Number" field="order_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Order Status" field="status" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Invoice Status" field="invoice_status" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Order Total (${currency})`} field="order_total" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Issued Date" field="created_at" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Invoice To" field="invoice_to" sortState={sortState} onSort={handleSort} />
          </tr></thead>
          <tbody>
            {sortData(invoiceData.orders || [], sortState.field, sortState.dir).map(o => (
              <tr key={o.order_number} className="border-b hover:bg-blue-50 cursor-pointer"
                onClick={async () => {
                  try {
                    const r = await api.get(`${PORTAL_API}/my-orders/${o.order_number}/detail`, authHeaders);
                    setViewPODetail(r.data);
                  } catch { toast.error('Could not load order details'); }
                }}>
                <td className="px-3 py-2">{o.order_number}</td>
                <td className="px-3 py-2"><Badge className="text-[10px]">{o.status}</Badge></td>
                <td className="px-3 py-2">{o.invoice_status}</td>
                <td className="px-3 py-2 text-right">${(o.order_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">{o.created_at ? new Date(o.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</td>
                <td className="px-3 py-2 text-slate-500">ingest@eyvo.org</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SupplierInvoiceApprovedTab({ invoiceData, token, onViewInvoice, currency = 'USD' }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [sortState, handleSort] = useSortState('invoice_date', 'desc');
  const approved = (invoiceData.data || []).filter(i => ['APPROVED','MATCHED','PAID','FULLY_MATCHED'].includes(i.status));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Approved Invoices</h2>
        <span className="text-xs text-slate-500">Total Records: {approved.length}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">This shows the list of invoices that have been approved.</p>
      <div className="border rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortableTableHeader label="Invoice Number" field="invoice_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Invoice Date" field="invoice_date" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Invoice Amount (${currency})`} field="amount" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Order Number" field="order_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Order Total (${currency})`} field="order_total" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Payment Date" field="payment_date" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Payment Amount (${currency})`} field="payment_amount" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Payment Status" field="payment_status" sortState={sortState} onSort={handleSort} />
            <th className="text-center px-3 py-2 w-10"></th>
          </tr></thead>
          <tbody>
            {sortData(approved, sortState.field, sortState.dir).map((inv, idx) => (
              <tr key={idx} className="border-b hover:bg-blue-50 cursor-pointer" onClick={async () => {
                try { const r = await api.get(`${PORTAL_API}/my-invoices/${inv.invoice_number || inv.invoice_ref}`, authHeaders); onViewInvoice(r.data); } catch { toast.error('Could not load invoice'); }
              }}>
                <td className="px-3 py-2">{inv.invoice_number}</td>
                <td className="px-3 py-2">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</td>
                <td className="px-3 py-2 text-right">{(inv.amount || inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">{inv.order_number || ''}</td>
                <td className="px-3 py-2 text-right">{(inv.order_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">{inv.payment_date || ''}</td>
                <td className="px-3 py-2 text-right">{(inv.payment_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">{inv.payment_status || 'NOT PAID'}</td>
                <td className="px-3 py-2 text-center"><Eye className="w-4 h-4 text-blue-500" /></td>
              </tr>
            ))}
            {approved.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-xs text-slate-400">No approved invoices found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SupplierInvoicePendingTab({ invoiceData, token, onViewInvoice, currency = 'USD' }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [sortState, handleSort] = useSortState('invoice_date', 'desc');
  const pending = (invoiceData.data || []).filter(i => !['APPROVED','MATCHED','PAID','FULLY_MATCHED'].includes(i.status));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Pending Invoices</h2>
        <span className="text-xs text-slate-500">Total Records: {pending.length}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">Invoices that have been submitted but not yet approved for payment.</p>
      <div className="border rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortableTableHeader label="Invoice Number" field="invoice_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Invoice Date" field="invoice_date" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Invoice Amount (${currency})`} field="amount" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Order Number" field="order_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label={`Order Total (${currency})`} field="order_total" sortState={sortState} onSort={handleSort} className="text-right" />
            <SortableTableHeader label="Status" field="status" sortState={sortState} onSort={handleSort} />
            <th className="text-center px-3 py-2 w-10"></th>
          </tr></thead>
          <tbody>
            {sortData(pending, sortState.field, sortState.dir).map((inv, idx) => (
              <tr key={idx} className="border-b hover:bg-blue-50 cursor-pointer" onClick={async () => {
                try { const r = await api.get(`${PORTAL_API}/my-invoices/${inv.invoice_number || inv.invoice_ref}`, authHeaders); onViewInvoice(r.data); } catch { toast.error('Could not load invoice'); }
              }}>
                <td className="px-3 py-2">{inv.invoice_number}</td>
                <td className="px-3 py-2">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</td>
                <td className="px-3 py-2 text-right">{(inv.amount || inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">{inv.order_number || ''}</td>
                <td className="px-3 py-2 text-right">{(inv.order_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2"><Badge className="text-[10px] bg-amber-100 text-amber-700">{inv.status || 'PENDING'}</Badge></td>
                <td className="px-3 py-2 text-center"><Eye className="w-4 h-4 text-blue-500" /></td>
              </tr>
            ))}
            {pending.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-slate-400">No pending invoices.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InvoiceDetailModal({ viewInvoice, onClose }) {
  if (!viewInvoice) return null;

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Invoice: {viewInvoice.invoice_number || viewInvoice.invoice_ref}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close invoice detail">&times;</button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {[
            ['Invoice Number', viewInvoice.invoice_number || viewInvoice.invoice_ref],
            ['Status', viewInvoice.status],
            ['Supplier', viewInvoice.supplier_name || viewInvoice.supplier_code],
            ['Invoice Date', viewInvoice.invoice_date ? new Date(viewInvoice.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''],
            ['Amount', viewInvoice.amount || viewInvoice.total ? `$${(viewInvoice.amount || viewInvoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''],
            ['Order Number', viewInvoice.order_number || ''],
            ['Payment Status', viewInvoice.payment_status || 'NOT PAID'],
            ['Payment Amount', `$${(viewInvoice.payment_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
            ['Source', viewInvoice.source || ''],
            ['Created', viewInvoice.created_at ? new Date(viewInvoice.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TZ_DEFAULT }) : ''],
          ].map(([label, val]) => (
            <div key={label}><span className="text-xs text-slate-500">{label}</span><p className="font-medium">{val || '—'}</p></div>
          ))}
        </div>
        {viewInvoice.line_items?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2">Line Items</h4>
            <table className="w-full text-xs border rounded">
              <thead><tr className="bg-slate-50 border-b">
                <th className="text-left px-2 py-1">Description</th>
                <th className="text-right px-2 py-1">Qty</th>
                <th className="text-right px-2 py-1">Price</th>
                <th className="text-right px-2 py-1">Total</th>
              </tr></thead>
              <tbody>
                {viewInvoice.line_items.map((li, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-2 py-1">{li.description || li.item_description || ''}</td>
                    <td className="px-2 py-1 text-right">{li.quantity || ''}</td>
                    <td className="px-2 py-1 text-right font-mono">{li.unit_price ? `$${li.unit_price}` : ''}</td>
                    <td className="px-2 py-1 text-right font-mono">{li.net_price ? `$${li.net_price}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 text-right">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
